import {makeAutoObservable} from "mobx";
import Order, {
    CombinationOrderLine, HalfAndHalfOrderLine,
    OrderLine,
    OrderLineAddons,
    OrderLineIngredients,
    OrderLineModifiers
} from "../@types/stores/order";
import {OrderType} from "../@types/enums/OrderType";
import {OrderStatus} from "../@types/enums/OrderStatus";
import ApiOrder, {
    ApiCombinationOrderLine, ApiHalfAndHalfOrderLine,
    ApiOrderLine,
    ApiOrderLine_addon,
    ApiOrderLine_ingredient
} from "../@types/api/order";
import apiOpen from "../api/open/index";
import {
    hideDialog,
    hideLoader,
    navigateTo,
    showAlert,
    showDialog,
    showError,
    showLoader
} from "../helpers/ComponentHelper";
import {CashRegisterPaymentMethod, Payment} from "../@types/api/payment";
import {sleep} from "../helpers/DateHelper";
import config from "../config/default.json";
import {addToLog, getCurrency, getEatInSettings, isApiOrderLine, isExpensiveFree} from "../helpers/ApiHelper";
import menuStore from "./menu";
import {PaymentMethod} from "../@types/enums/PaymentType";
import ApiCustomer from "../@types/api/customer";
import {t} from "i18next";
import {Combination, MenuItem, MenuItemCombination} from "../@types/stores/menu";
import {v4 as guid} from 'uuid';
import {LogLevel} from "../@types/enums/logLevel";
import ApiInternalCard from "../@types/api/internalCard";


class OrderStore {
    order: Order = this.emptyOrder;
    paymentId: string = "";
    paymentMethodId: number = 0;
    terminalId: string | undefined;
    qr: string = "";
    internalCard: string = "";
    internalCardObj: ApiInternalCard | null = null;
    currentOrderLine: OrderLine | null = null;
    currentCombinationOrderLine: CombinationOrderLine | null = null;
    editComboId: string = "";
    customerCode: string = "";
    customer: ApiCustomer | null = null;
    allowedPaymentMethods: CashRegisterPaymentMethod[] = [];
    amountToPay: number = 0;
    isFullyPaid: boolean = false;
    tableNumber: number = -1;

    constructor() {
        makeAutoObservable(this);
    }

    public setType(type: OrderType): void {
        this.order.type = type;
    }

    public setTableNumber(tableNumber: number): void {
        this.tableNumber = tableNumber;
    }

    public get emptyOrder(): Order {
        return {
            id: guid(),
            type: OrderType.EAT_IN,
            lines: [],
            status: OrderStatus.NEW,
            total: 0,
            vatAmount: 0,
            kitchenNumber: 0,
            paidSum: 0,
            discount: 0
        }
    }

    private getApiArticleOrderLineModifiers = (modifiers: OrderLineModifiers[]): number[] => {
        const _modifiers: number[] = [];
        modifiers.forEach(x => x.items.forEach(m => {
            _modifiers.push(m);
        }));
        return _modifiers;
    }

    private getArticleOrderLineAddons = (addons: OrderLineAddons[]): ApiOrderLine_addon[] => {
        const _addons: ApiOrderLine_addon[] = [];
        addons.forEach(x => x.items.forEach(a => {
            const _a: ApiOrderLine_addon = {
                number: a.number,
                quantity: a.quantity,
                unitPrice: !!a.isFree ? 0 : (a.price || 0)
            };
            _addons.push(_a);
        }));
        return _addons;
    }

    private getArticleOrderLineIngredients = (ingredients: OrderLineIngredients[]): ApiOrderLine_ingredient[] => {
        const _ingredients: ApiOrderLine_ingredient[] = [];
        ingredients.forEach(ig => {
            ig.items.forEach(i => {
                if (i.default && i.quantity > 0) {
                    // DO NOT add default ingredients w/o changes
                } else {
                    const _i: ApiOrderLine_ingredient = {
                        number: i.number,
                        quantity: i.quantity,
                        sellUnit: i.sellUnit,
                        unitPrice: i.price
                    };
                    _ingredients.push(_i);
                }
            });
        });
        return _ingredients;
    }

    public async addToOrder(line: OrderLine): Promise<boolean> {
        this.order.lines.push(line);
        this.order.total += line.total * line.count;

        const modifiers: number[] = line.modifiers ? this.getApiArticleOrderLineModifiers(line.modifiers) : [];
        const addons: ApiOrderLine_addon[] = line.addons ? this.getArticleOrderLineAddons(line.addons) : [];
        const ingredients: ApiOrderLine_ingredient[] = line.ingredients ? this.getArticleOrderLineIngredients(line.ingredients) : [];

        const orderLine: ApiOrderLine = {
            id: line.id,
            articleNumber: line.number,
            takeAway: this.order.type === OrderType.TAKEAWAY,
            unitPrice: line.price,
            discount: 0,
            discountName: "",
            comment: "",
            quantity: line.count,
            weight: 0,
            employeeId: 0,
            weightUnit: 0,
            modifiers,
            addons,
            components: line.components?.map(x => {
                return {
                    number: x.number,
                    quantity: x.quantity,
                    sellUnit: x.sellUnit
                }
            }),
            ingredients
        };
        const apiOrder = await apiOpen.addOrderLine(this.order.id, orderLine);

        if (apiOrder) {
            this.convertApiOrderToOrder(apiOrder);
            localStorage.setItem("order", JSON.stringify(orderStore.order));

            addToLog(`Add article to order ${orderLine.articleNumber} / ${line.name}, count = ${orderLine.quantity}`);
            addToLog(`UI OrderLine: ${JSON.stringify(orderLine)}`, LogLevel.DEBUG);
        } else {
            this.order.total -= line.total * line.count;
            this.order.lines = this.order.lines.filter(x => x.id !== line.id);

        }

        this.currentOrderLine = null;

        return !apiOrder || typeof apiOrder?.lastErrorResponseCode === "number" ? false : true;
    }

    public async addCombinationToOrder(line: CombinationOrderLine): Promise<boolean> {
        if (this.editComboId) {
            await orderStore.subOrderLineQuantity(this.editComboId);
            this.editComboId = "";
        }

        this.order.lines.push(line);
        this.order.total += line.total * line.count;

        const combinationOrderLines: ApiOrderLine[] = [];
        for (const step of line.steps) {
            for (const article of step.articles) {
                if (article.articleId === -1) continue;

                const modifiers: number[] = article.modifiers ? this.getApiArticleOrderLineModifiers(article.modifiers) : [];
                const addons: ApiOrderLine_addon[] = article.addons ? this.getArticleOrderLineAddons(article.addons) : [];
                const ingredients: ApiOrderLine_ingredient[] = article.ingredients ? this.getArticleOrderLineIngredients(article.ingredients) : [];

                const articleOrderLine: ApiOrderLine = {
                    id: article.id,
                    articleNumber: article.number,
                    takeAway: this.order.type === OrderType.TAKEAWAY,
                    unitPrice: article.price,
                    discount: 0,
                    discountName: "",
                    comment: "",
                    quantity: article.count,
                    weight: 0,
                    employeeId: 0,
                    weightUnit: 0,
                    modifiers,
                    addons,
                    components: article.components?.map(x => {
                        return {
                            number: x.number,
                            quantity: x.quantity,
                            sellUnit: x.sellUnit
                        }
                    }),
                    ingredients
                };

                combinationOrderLines.push(articleOrderLine);
            }
        }

        const orderLine: ApiCombinationOrderLine = {
            id: line.id,
            internalId: line.combinationId,
            unitPrice: line.price,
            quantity: line.count,
            takeAway: this.order.type === OrderType.TAKEAWAY,
            discount: 0,
            discountName: "",
            employeeId: 0,
            combination: combinationOrderLines
        };

        const apiOrder = await apiOpen.addCombinationOrderLine(this.order.id, orderLine);

        if (apiOrder) {
            this.convertApiOrderToOrder(apiOrder);
            localStorage.setItem("order", JSON.stringify(orderStore.order));
            addToLog(`Add combination to order #${orderLine.id} / ${orderLine.internalId} / ${line.name}, count = ${orderLine.quantity}`);
            addToLog(`OrderLine: ${JSON.stringify(orderLine)}`, LogLevel.DEBUG);
        } else {
            this.order.total -= line.total * line.count;
            this.order.lines = this.order.lines.filter(x => x.id !== orderLine.id);
        }

        this.currentCombinationOrderLine = null;

        return !apiOrder || typeof apiOrder?.lastErrorResponseCode === "number" ? false : true;
    }

    public getExtraPriceFromApiOrderLine(line: ApiOrderLine): number {
        // Addons
        let addonPrice = 0;
        line.addons?.forEach(a => {
            addonPrice += (a.unitPrice || 0) * a.quantity;
        });

        // Ingredients
        let ingredientPrice = 0;
        line.ingredients.forEach(i => {
            ingredientPrice += (i.unitPrice || 0) * i.quantity;
        });

        return addonPrice + ingredientPrice;
    }

    public convertApiOrderToOrder(apiOrder: ApiOrder, oldOrderLineId?: string): void {
        let total = 0;
        const newLines: (OrderLine | CombinationOrderLine | HalfAndHalfOrderLine)[] = [];

        apiOrder.orderLines.forEach(ol => {
            let line = this.order.lines.find(x => x.id === ol.id);
            if (!line && oldOrderLineId)
                line = this.order.lines.find(x => x.id === oldOrderLineId);

            if (line) {
                line.id = ol.id;
                line.price = ol.unitPrice;
                line.count = ol.quantity;
                line.subsidyName = ol.subsidyName;
                line.subsidy = ol.subsidy;

                if (isApiOrderLine(ol)) {
                    const extraPrice = this.getExtraPriceFromApiOrderLine(ol);

                    line.total = (line.price + extraPrice) * line.count;
                    line.discount = ol.discount;
                }
                else {
                    let extraPrices = 0;
                    const comboOrderLine = ol as unknown as ApiCombinationOrderLine;
                    comboOrderLine.combination.forEach(combination => {
                        extraPrices += this.getExtraPriceFromApiOrderLine(combination) * combination.quantity;
                    });

                    line.total = (line.price + extraPrices) * line.count;
                    line.discount = ol.discount;
                }

                newLines.push(line);
                total += line.total;
            }
        });

        this.order.lines = newLines;
        this.order.id = apiOrder.orderId;
        this.order.total = apiOrder.totalSum ? apiOrder.totalSum : total;
        this.order.discount = apiOrder.discount;
        this.order.vatAmount = apiOrder.totalVat || 0;
        this.order.totalSubsidySum = apiOrder.totalSubsidySum;
    }

    public async cancelOrder() {
        showLoader();
        if (this.order.id && this.order.status !== OrderStatus.NEW) {
            const id = this.order.id;
            this.order.id = "";
            await apiOpen.cancelOrder(id);
            addToLog(`Cancel order ${id}`);
            await apiOpen.sendLogs(id);
        }
        this.resetOrder();
        hideLoader();
    }

    public resetOrder() {
        this.order = this.emptyOrder;
        this.paymentId = "";
        this.setDefaultPaymentMethodId();
        this.internalCard = "";
        this.qr = "";
        this.customerCode = "";
        this.customer = null;
        this.amountToPay = 0;
        this.isFullyPaid = false;
        this.currentOrderLine = null;
        this.currentCombinationOrderLine = null;
        this.editComboId = "";
        this.internalCardObj = null;
        this.tableNumber = -1;

        localStorage.removeItem("orderId");
        localStorage.removeItem("order");
        localStorage.removeItem("orderId_createdAt");

        menuStore.resetNavigation();
    }

    public async createOrder(): Promise<ApiOrder | null> {
        let cashRegisterId = +(localStorage.getItem("cashRegisterId") || 0);
        if (isNaN(cashRegisterId) || !cashRegisterId) {
            console.error("CashRegisterId not set");
            showError("CashRegisterId not set");
            return null;
        }

        const _order: ApiOrder = {
            orderId: this.order.id,
            cashRegisterId,
            employeeId: 0,
            external: true,
            orderLines: [],
            discount: 0,
            paidSum: 0,
            discountMode: 1
        };

        const apiOrder = await apiOpen.createOrder(_order);
        if (apiOrder) {
            this.order.status = OrderStatus.DRAFT;
            this.order.kitchenNumber = apiOrder.kitchenNumber || 0;
        }

        addToLog(`Order created #${apiOrder?.orderId || "<unknown>"}`);

        return apiOrder;
    }

    public async removeOrderLine(orderLineId: string): Promise<boolean> {
        const apiOrder = await apiOpen.removeOrderLine(this.order.id, orderLineId);
        if (apiOrder) {
            this.convertApiOrderToOrder(apiOrder);
            localStorage.setItem("order", JSON.stringify(orderStore.order));
        }
        return !!apiOrder;
    }

    public async addOrderLineQuantity(orderLineId: string): Promise<boolean> {
        const apiOrder = await apiOpen.addOrderLineQuantity(this.order.id, orderLineId);

        if (apiOrder) {
            this.convertApiOrderToOrder(apiOrder);
            localStorage.setItem("order", JSON.stringify(orderStore.order));
        }
        return !!apiOrder;
    }

    public async subOrderLineQuantity(orderLineId: string): Promise<boolean> {
        const apiOrder = await apiOpen.subOrderLineQuantity(this.order.id, orderLineId);

        if (apiOrder) {
            this.convertApiOrderToOrder(apiOrder, orderLineId);
            localStorage.setItem("order", JSON.stringify(orderStore.order));
        }
        return !!apiOrder;
    }

    public async startPayment(): Promise<boolean> {
        if (this.order.status === OrderStatus.PAYMENT_PENDING && this.paymentId) {
            return true;
        }

        this.order.status = OrderStatus.START_PAYMENT;

        const payment: Payment = {
            paymentMethodId: this.paymentMethodId,
            paymentId: guid(),
            printReceiptStrategy: 11,
            printKitchenTicket: true,
            internalCardNumber: this.getPaymentMethod()?.type === PaymentMethod.GIFT_CARD ? this.internalCard : undefined,
            amountToPay: this.amountToPay
        };

        if (this.terminalId && this.terminalId !== "internalCard")
            payment.terminalId = this.terminalId;

        if (getEatInSettings()?.manualKitchenNumber) {
            payment.eatIn = {
                kitchenNumber: this.tableNumber
            };
        }

        this.paymentId = payment.paymentId;

        const r = await apiOpen.sendPayment(this.order.id, payment);

        if (r?.paymentQrCode)
            this.qr = r.paymentQrCode;

        if (r?.kitchenNumber)
            this.order.kitchenNumber = r.kitchenNumber;

        if (r?.errorCode) {
            // PART PAYMENT DIALOG
            if (r.errorCode === 12) {
                if (r.availableAmount && r.availableAmount > 0) {
                    this.order.status = OrderStatus.PART_PAYMENT;
                    showDialog(t("notEnoughBalanceTitle"), `${t("cardBalance")}: <b style="font-size: 20px;">${r.availableAmount}</b> ${getCurrency()}<br/>` + t("notEnoughBalanceText"),
                        // ON YES
                        async () => {
                            this.amountToPay = r.availableAmount as number;
                            hideDialog();
                            showLoader();
                            const r2 = await this.startPayment();
                            hideLoader();
                            if (r2 || (!r2 && this.order.status !== OrderStatus.PART_PAYMENT)) {
                                this.order.status = OrderStatus.DRAFT;
                                this.paymentId = "";
                                this.changeInternalCard("");
                                this.setDefaultPaymentMethodId();

                                navigateTo("/paymentMethod");
                            }
                        },
                        // ON NO
                        () => {
                            this.order.status = OrderStatus.DRAFT;
                            this.paymentId = "";
                            this.changeInternalCard("");
                            this.setDefaultPaymentMethodId();
                            this.amountToPay = 0;
                            hideDialog();
                            navigateTo("/paymentMethod");
                        }
                    );
                } else {
                    this.order.status = OrderStatus.PAYMENT_FAILED;
                    showAlert(t("notEnoughBalanceTitle"), `${t("cardBalance")}: <b style="font-size: 20px;">0</b> ${getCurrency()}<br/>` + t("chooseAnotherPaymentMethod"));
                }
            } else {
                this.order.status = OrderStatus.PAYMENT_FAILED;
                showError(`${r.errorCode} - ${r.errorMessage}`);
                return false;
            }
        }

        let paidSum = 0;
        if (typeof r?.isFullyPaid === "boolean" && !r.isFullyPaid) {
            const order = await apiOpen.getOrder(this.order.id);
            if (order?.paidSum) {
                this.order.paidSum = order.paidSum;
                paidSum = order.paidSum;
            }
        }

        if (r?.transactionInProcess) {
            this.amountToPay = 0;
            this.order.status = OrderStatus.PAYMENT_PENDING;
            this.isFullyPaid = r.isFullyPaid;
            return true;
        }

        if (r?.transactionSuccessfully) {
            this.amountToPay = 0;
            this.order.status = OrderStatus.PAYMENT_SUCCESS;
            this.isFullyPaid = r.isFullyPaid;

            if (this.internalCardObj && this.getPaymentMethod()?.type === PaymentMethod.GIFT_CARD && this.internalCardObj.cardNumber === this.internalCard) {
                this.internalCardObj.balance -= paidSum;
            }

            return true;
        }

        if (!r) {
            this.order.status = OrderStatus.PAYMENT_FAILED;
        }

        return false;
    }

    public async waitPaymentFinish(): Promise<boolean> {
        const r = await apiOpen.getPaymentStatus(this.paymentId);

        if (!r) {
            this.order.status = OrderStatus.PAYMENT_FAILED;
            showError(`API error`);
            return false;
        }

        if (r?.transactionSuccessfully) {
            this.isFullyPaid = r.isFullyPaid;
            if (r?.kitchenNumber)
                this.order.kitchenNumber = r.kitchenNumber;

            this.order.status = OrderStatus.PAYMENT_SUCCESS;
            return true;
        }

        if (r?.errorCode) {
            this.order.status = OrderStatus.PAYMENT_FAILED;
            return false;
        }

        await sleep(config.payment.polingStatusDelay);
        const st = await this.waitPaymentFinish();
        return st;
    }

    public async printReceipt() {
        await apiOpen.printReceipt(this.order.id);
    }

    public set setCurrentOrderLine(orderLine: OrderLine) {
        this.currentOrderLine = orderLine;
    }

    public reCalcOrderLine(orderLine: OrderLine): {orderLine: OrderLine, articlePrice: number, addonsPrice: number, ingredientsPrice: number} {
        const itemPrice = orderLine.price * orderLine.count;

        // Addons
        let addonsPrice = 0;

        const addonsPrices: { price: number, id: number, groupId: number }[] = [];
        const groupFree: { groupId: number, free: number }[] = [];
        orderLine.addons?.forEach(ag => {
            groupFree.push({
                groupId: ag.groupId,
                free: ag.groupFreeAddons
            });

            ag.items.forEach(a => {
                const addonPrice = a.price || 0;
                addonsPrice += addonPrice;

                if (ag.groupFreeAddons > 0) {
                    for (let i = 1; i <= a.quantity; i++) {
                        addonsPrices.push({
                            id: a.id,
                            groupId: ag.groupId,
                            price: a.price || 0
                        });
                    }
                }
            });
        });

        // free addons
        if (isExpensiveFree()) {
            addonsPrices.sort((a, b) => {
                if (a.price > b.price) return 1;
                if (a.price < b.price) return -1;
                return 0;
            });
        } else {
            addonsPrices.sort((a, b) => {
                if (a.price < b.price) return 1;
                if (a.price > b.price) return -1;
                return 0;
            });
        }

        let articleFreeAddons = 0;
        if (orderLine.menuId && orderLine.subMenuId) {
            let item = menuStore.getItemByIds(orderLine.menuId, orderLine.subMenuId, orderLine.number);
            articleFreeAddons = item?.freeAddons || 0;
        }
        if (orderLine.recommendationGroupId) {
            const item = menuStore.getRecommendationItemByNumber(orderLine.recommendationGroupId, orderLine.number);
            articleFreeAddons = item?.freeAddons || 0;
        }

        let i = 0;
        let fullFreePrice = 0;
        while (articleFreeAddons > 0 && i < addonsPrices.length) {
            const ap = addonsPrices[i];
            const gf = groupFree.find(x => x.groupId === ap.groupId);
            if (gf && gf.free > 0) {
                gf.free--;
                articleFreeAddons--;
                fullFreePrice += ap.price;

                const addonGroup = orderLine.addons?.find(ag => ag.groupId === ap.groupId);
                if (addonGroup) {
                    const addon = addonGroup.items.find(a => a.id === ap.id);
                    if (addon)
                        addon.isFree = true;
                }
            }

            i++;
        }

        addonsPrice -= fullFreePrice;

        // Ingredients
        let ingredientsPrice = 0;
        orderLine.ingredients?.forEach(ig => {
            ig.items.forEach(i => {
                if (!i.default || (i.quantity < 0 && i.default))
                    ingredientsPrice += i.quantity * i.price;
            });
        });

        orderLine.total = itemPrice + addonsPrice + ingredientsPrice;
        orderLine.extraPrice = addonsPrice + ingredientsPrice;
        return {
            orderLine,
            articlePrice: itemPrice,
            addonsPrice,
            ingredientsPrice
        };
    }

    reCalcCurrentOrderLine() {
        if (this.currentOrderLine)
            this.currentOrderLine = this.reCalcOrderLine(this.currentOrderLine).orderLine;
    }

    changeCustomerCode(code: string) {
        this.customerCode = code;
    }

    changeInternalCard(code: string) {
        this.internalCard = code;
    }

    async attachCustomerToOrder(): Promise<boolean> {
        if (this.customerCode) {
            return await apiOpen.attachCustomerToOrder(this.order.id, this.customerCode, false);
        }

        return true;
    }

    async attachInternalCardToOrder(): Promise<boolean> {
        if (this.customerCode) {
            return await apiOpen.attachInternalCardToOrder(this.order.id, this.customerCode);
        }

        return true;
    }

    async getAllowedPaymentMethods() {
        const _allowedPaymentMethods = await apiOpen.getPaymentMethods(!this.allowedPaymentMethods.length);
        if (Array.isArray(_allowedPaymentMethods)) {
            this.allowedPaymentMethods = _allowedPaymentMethods;
        }

        this.setDefaultPaymentMethodId();
    }

    setDefaultPaymentMethodId() {
        if (this.allowedPaymentMethods.length) {
            // if set current internal card -> choose this one
            // if (this.internalCardObj) {
            //     this.paymentMethodId = this.internalCardObj.paymentMethodId;
            //     this.terminalId = "internalCard";
            //
            //     addToLog(`Default payment method = #${this.internalCardObj.paymentMethodId} / Internal card`);
            // }
            // else {
                this.paymentMethodId = this.allowedPaymentMethods[0].id;
                this.terminalId = this.allowedPaymentMethods[0].terminalId;

                addToLog(`Default payment method = #${this.allowedPaymentMethods[0].id} / ${this.allowedPaymentMethods[0].name}`);
            // }
        }
    }

    getPaymentMethod(): CashRegisterPaymentMethod | null {
        if (this.terminalId === "internalCard")
            return this.allowedPaymentMethods.find(x => x.id === this.paymentMethodId) || null;
        return this.allowedPaymentMethods.find(x => x.id === this.paymentMethodId && x.terminalId === this.terminalId) || null;
    }

    async cancelPayment(): Promise<boolean> {
        if (this.paymentId) {
            return await apiOpen.cancelPayment(this.paymentId);
        }
        return false;
    }

    async reloadOrder(orderId: string) {
        try {
            const _order = JSON.parse(localStorage.getItem("order") || "null");

            const r = await apiOpen.getOrder(orderId);
            if (_order && r) {
                this.order = _order;
                this.convertApiOrderToOrder(r);
                return;
            }
        } catch (e) {
            // ignore
        }

        this.resetOrder();
        window.location.href = "/";
    }

    addArticleToCurrentCombinationOrderLine = (combination: MenuItemCombination, step?: Combination, orderLine?: OrderLine, changedArticleNumber?: string) => {
        if (!this.currentCombinationOrderLine) {
            this.currentCombinationOrderLine = {
                id: guid(),
                combinationId: combination.id,
                count: 1,
                price: combination.price || 0,
                total: combination.price || 0,
                img: combination.previewEdit,
                name: combination.name,
                menuId: menuStore.navigation.category,
                subMenuId: menuStore.navigation.subCategory,
                steps: [],
                isFixedPrice: !!combination.price
            };
        }

        if (!step || !orderLine) return;

        let olStep = this.currentCombinationOrderLine.steps.find(x => x.id === step.id);
        if (!olStep) {
            olStep = {
                id: step.id,
                name: step.name,
                articles: [orderLine]
            };
            this.currentCombinationOrderLine.steps.push(olStep);
        }
        else {
            if (changedArticleNumber) {
                const oldOrderLine = olStep.articles.find(x => x.number === changedArticleNumber);
                olStep.articles = olStep.articles.filter(x => x.number !== changedArticleNumber);

                if (oldOrderLine) {
                    if (!combination.price) {
                        this.currentCombinationOrderLine.price -= oldOrderLine.price * oldOrderLine.count;
                        this.currentCombinationOrderLine.total -= oldOrderLine.total;
                    }
                    else {
                        this.currentCombinationOrderLine.total -= oldOrderLine.extraPrice || 0;
                    }
                }
            }
            olStep.articles.push(orderLine);
        }

        if (!combination.price) {
            this.currentCombinationOrderLine.price += orderLine.price * orderLine.count;
            this.currentCombinationOrderLine.total += orderLine.total;
        }
        else {
            this.currentCombinationOrderLine.total += orderLine.extraPrice || 0;
        }
    }

    // package combination -> all articles of all steps are always included
    fillPackageCombinationOrderLine = (combination: MenuItemCombination, menuId: number, subMenuId: number) => {
        if (!combination.isPackage) return;

        const isFilled = this.currentCombinationOrderLine?.combinationId === combination.id
            && combination.combination.every(step => {
                const olStep = this.currentCombinationOrderLine?.steps.find(x => x.id === step.id);
                return !!olStep && step.articles.every(ca => olStep.articles.some(a => a.articleId === ca.article.id));
            });

        if (isFilled) return;

        this.currentCombinationOrderLine = null;
        this.addArticleToCurrentCombinationOrderLine(combination);

        for (const step of combination.combination) {
            for (const ca of step.articles) {
                this.addArticleToCurrentCombinationOrderLine(combination, step, {
                    id: guid(),
                    articleId: ca.article.id,
                    combinationId: combination.id,
                    menuId,
                    subMenuId,
                    name: ca.article.name,
                    number: ca.article.number,
                    count: step.price ? 1 : ca.quantity,
                    price: step.price || ca.article.price,
                    total: step.price || ca.article.price * ca.quantity,
                    img: ca.article.previewList,
                    components: ca.article.components
                });

                if (ca.article.recommendedGroups)
                    menuStore.addCombinationRecommendationGroupIds(ca.article.recommendedGroups);
            }
        }

        addToLog(`Open package combination ${combination.id} / ${combination.name}`);
    }

    getCurrentCombinationArticleOrderLine = (step: Combination, article: MenuItem): OrderLine | undefined => {
        const _step = this.currentCombinationOrderLine?.steps.find(x => x.id === step.id);
        if (!_step) return;

        return _step.articles.find(x => x.number === article.number);
    }

    updateCurrentCombinationArticleOrderLine = (combination: MenuItemCombination, step: Combination, orderLine: OrderLine) => {
        if (!this.currentCombinationOrderLine) return;

        const _step = this.currentCombinationOrderLine?.steps.find(x => x.id === step.id);
        if (!_step) return;

        const oldOrderLine = _step.articles.find(x => x.id === orderLine.id);
        if (oldOrderLine) {
            if (!combination.price) {
                this.currentCombinationOrderLine.price -= oldOrderLine.price * oldOrderLine.count;
                this.currentCombinationOrderLine.total -= oldOrderLine.total;
            }
            else {
                this.currentCombinationOrderLine.total -= oldOrderLine.extraPrice || 0;
            }
        }

        _step.articles = _step.articles.filter(x => x.id !== orderLine.id);
        orderLine = this.reCalcOrderLine(orderLine).orderLine;
        this.addArticleToCurrentCombinationOrderLine(combination, step, orderLine);
    }

    resetCurrentLines = () => {
        this.currentOrderLine = null;
        this.currentCombinationOrderLine = null;
    }

    public getExtraPriceFromOrderLine(line: OrderLine): number {
        // Addons
        let addonPrice = 0;
        line.addons?.forEach(ag => {
            ag.items.filter(a => !a.isFree).forEach(a => {
                addonPrice += (a.price || 0) * a.quantity;
            });
        });

        // Ingredients
        let ingredientPrice = 0;
        line.ingredients?.forEach(ig => {
            ig.items.forEach(i => {
                if (i.default && i.quantity > 0) {
                    // DO NOT add default ingredients w/o changes
                }
                else
                    ingredientPrice += (i.price || 0) * i.quantity;
            });
        });

        return addonPrice + ingredientPrice;
    }

    public async addHalfAndHalfToOrder(line: HalfAndHalfOrderLine): Promise<boolean> {
        this.order.lines.push(line);
        this.order.total += line.total * line.count;

        const combinationOrderLines: ApiOrderLine[] = [];
        for (const step of line.steps) {
            for (const article of step.articles) {
                if (article.articleId === -1) continue;

                const modifiers: number[] = article.modifiers ? this.getApiArticleOrderLineModifiers(article.modifiers) : [];
                const addons: ApiOrderLine_addon[] = article.addons ? this.getArticleOrderLineAddons(article.addons) : [];
                const ingredients: ApiOrderLine_ingredient[] = article.ingredients ? this.getArticleOrderLineIngredients(article.ingredients) : [];

                const articleOrderLine: ApiOrderLine = {
                    id: article.id,
                    articleNumber: article.number,
                    takeAway: this.order.type !== OrderType.EAT_IN,
                    unitPrice: article.price,
                    discount: 0,
                    discountName: "",
                    comment: "",
                    quantity: article.count,
                    weight: 0,
                    employeeId: 0,
                    weightUnit: 0,
                    modifiers,
                    addons,
                    components: article.components?.map(x => {
                        return {
                            number: x.number,
                            quantity: x.quantity,
                            sellUnit: x.sellUnit
                        }
                    }),
                    ingredients
                };

                combinationOrderLines.push(articleOrderLine);
            }
        }

        const orderLine: ApiHalfAndHalfOrderLine = {
            id: line.id,
            unitPrice: line.price,
            quantity: line.count,
            takeAway: this.order.type !== OrderType.EAT_IN,
            discount: 0,
            discountName: "",
            employeeId: 0,
            combination: combinationOrderLines
        };

        const apiOrder = await apiOpen.addHalfAndHalfOrderLine(this.order.id, orderLine);

        if (apiOrder) {
            this.convertApiOrderToOrder(apiOrder);
            localStorage.setItem("order", JSON.stringify(orderStore.order));

            addToLog(`Add half-and-half to order`);
            addToLog(`UI OrderLine: ${JSON.stringify(orderLine)}`, LogLevel.DEBUG);
        } else {
            this.order.total -= line.total * line.count;
            this.order.lines = this.order.lines.filter(x => x.id !== line.id);

        }

        this.currentOrderLine = null;

        return !apiOrder || typeof apiOrder?.lastErrorResponseCode === "number" ? false : true;
    }
}

const orderStore = new OrderStore();
export default orderStore;
